import { GlobalStoreStateConfiguration } from '../stores/globalStore';
import {
  Accordion,
  AnalyticsManager,
  Error,
  OneHubEventBuilder,
  OneHubEventGroups,
  OneHubEventTypes,
  Video,
} from '@tribal-worldwide-london/fa-analytics';
import {
  getAnalyticsConfigurationFromCarConfiguration,
  getAnalyticsErrorFields,
  getAnalyticsPartnerFields,
} from './utils';
import { FoundRetailers } from '@tribal-worldwide-london/fa-retailer-service-vwg';

export interface EventCommonProps {
  pageName?: string;
  sectionName?: string;
  url?: string | null;
  label?: string;
  configuration?: GlobalStoreStateConfiguration | undefined | null;
  configurations?: GlobalStoreStateConfiguration[] | undefined | null;
  retailers?: FoundRetailers[];
  analyticsManager: AnalyticsManager;
  analyticsBuilder: OneHubEventBuilder;
  contentId?: string;
}

export type EventAccordionProps = EventCommonProps & {
  accordionFields?: Accordion;
};
export type EventVideoProps = EventCommonProps & { videoFields?: Video };
export type EventErrorProps = EventCommonProps & { error?: Error };

export type EventProps = EventCommonProps &
  EventVideoProps &
  EventAccordionProps &
  EventErrorProps;

const VWEvent = (eventName: OneHubEventTypes, eventProps: EventProps) => {
  const {
    pageName = 'Online PB',
    sectionName,
    url,
    label,
    configurations,
    configuration,
    retailers,
    analyticsManager,
    analyticsBuilder,
    error,
    accordionFields,
    videoFields,
    contentId,
  } = eventProps;

  const partnerFields = retailers ? getAnalyticsPartnerFields(retailers) : null;
  const errorFields = error ? getAnalyticsErrorFields(error) : null;

  const eventData = analyticsBuilder
    .reset()
    .addEventValue(OneHubEventGroups.Event_Info, 'eventAction', eventName)
    .addEventValue(OneHubEventGroups.Event_Info, 'pageName', pageName, true)
    .addEventValue(OneHubEventGroups.Event_Info, 'sectionName', sectionName)
    .addEventValue(OneHubEventGroups.Link, 'url', url)
    .addEventValue(OneHubEventGroups.Link, 'name', label);

  if (contentId) {
    eventData.addEventValue(
      OneHubEventGroups.Event_Info,
      'contentId',
      contentId
    );
  }

  if (configuration) {
    eventData.addConfiguration(
      getAnalyticsConfigurationFromCarConfiguration(configuration)
    );
  } else {
    const analyticsConfigurations =
      configurations?.map((configuration) =>
        getAnalyticsConfigurationFromCarConfiguration(configuration)
      ) ?? null;

    eventData.addConfigurations(analyticsConfigurations);
  }

  if (partnerFields) eventData.addPartner(partnerFields);
  if (videoFields) eventData.addVideo(videoFields);
  if (accordionFields) eventData.addAccordion(accordionFields);
  if (errorFields) eventData.addError(errorFields);

  analyticsManager.sendEvent(eventName, eventData.getOutput());
};

export const performVWBasicDownloadLinkClick = (
  clickEventProps: EventCommonProps
) => {
  VWEvent(OneHubEventTypes.VWBasic_Download_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
};

export function performVWBasicButtonClick(clickEventProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_Button_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWInfoButtonCLick(clickEventProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_InfoButton_Click, clickEventProps);
}

export function performVWBasicFindDealerSearchClick(
  clickEventProps: EventCommonProps
) {
  VWEvent(OneHubEventTypes.VWBasic_FindDealerSearch_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWBasicDealerButtonClick(
  clickEventProps: EventCommonProps
) {
  VWEvent(OneHubEventTypes.VWBasic_DealerButton_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWBasicMultipleVehiclesButtonClick(
  clickEventProps: EventCommonProps
) {
  VWEvent(OneHubEventTypes.VWBasic_MultipleVehiclesButton_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWBasicLinkClick(clickEventProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_Link_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWBasicPageLoad(pageLoadProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_Pageload, pageLoadProps);
}

export function performVWBasicMultipleVehiclesPageLoad(
  pageLoadProps: EventCommonProps
) {
  VWEvent(OneHubEventTypes.VWBasic_MultipleVehicles_Pageload, pageLoadProps);
}

export function performVWAccordionOpenClick(
  clickEventProps: EventAccordionProps
) {
  VWEvent(OneHubEventTypes.VWBasic_AccordionOpen_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWAccordionCloseClick(
  clickEventProps: EventAccordionProps
) {
  VWEvent(OneHubEventTypes.VWBasic_AccordionClose_Click, {
    ...clickEventProps,
    url: window.location.href,
  });
}

export function performVWBasicLayerLoad(layerLoadProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_Layer_Load, {
    ...layerLoadProps,
    label: 'Layer Open',
    url: window.location.href
  });
}

export function performVWBasicLayerClose(layerLoadProps: EventCommonProps) {
  VWEvent(OneHubEventTypes.VWBasic_LayerClose_Click, {
    ...layerLoadProps,
    url: window.location.href,
    label: 'Layer Close',
  });
}

export function performVWBasicVideoPlayerPlayClick(
  eventProps: EventVideoProps
) {
  VWEvent(OneHubEventTypes.VideoPlayer_VideoPlay_Click, eventProps);
}

export function performVWBasicVideoPlayerPauseClick(
  eventProps: EventVideoProps
) {
  VWEvent(OneHubEventTypes.VideoPlayer_VideoPause_Click, eventProps);
}

export function performVWBasicVideoPlayerMilestoneReached(
  eventProps: EventVideoProps
) {
  VWEvent(OneHubEventTypes.VideoPlayer_VideoMilestone_Reached, eventProps);
}

export function performVWBasicGenericFeatureAppLoad(
  featureAppLoadProps: EventCommonProps
) {
  VWEvent(OneHubEventTypes.VWBasic_GenericFeatureApp_Load, featureAppLoadProps);
}

export function performVWBasicErrorMessageLoad(
  errorMessageLoadProps: EventErrorProps
) {
  VWEvent(OneHubEventTypes.VWBasic_ErrorMessage_Load, errorMessageLoadProps);
}
